import React, { type MouseEvent, useRef, useState } from 'react';
import { ApplicationView } from '@aha-app/mvc';
import { useEventHandler } from '@aha-app/aha-editor-v2';
import styles from './ResizerHandle.module.css';
import classNames from 'classnames';

// Resize the artifact panel.
const ResizerHandle = ({
  onResize,
  onStartResize,
  onEndResize,
  floating = false,
}: {
  onResize: (number) => void;
  onStartResize: () => void;
  onEndResize: () => void;
  floating?: boolean;
}) => {
  const handleRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [startX, setStartX] = useState<number>(0);
  const [startWidth, setStartWidth] = useState<number>(0);

  const width = (x: number, deltaX?: number) => {
    if (!handleRef.current?.parentElement?.parentElement) return 'inherit';

    if (floating) {
      if (deltaX) {
        const newWidth = startWidth + deltaX;
        return newWidth;
      }
    } else {
      const parentRect =
        handleRef.current.parentElement.parentElement.getBoundingClientRect();
      const percentage = 100 - (100 * x) / parentRect.right;

      // Adjust for the margin on the right of the artifact panel and the offset of the .resizer.
      return `calc(${Math.min(
        Math.max(percentage, 20),
        80
      )}vw - var(--spacer-2) - 3px)`;
    }
  };

  const onMouseDown = (event: MouseEvent) => {
    setDragging(true);
    setStartX(event.clientX);
    setStartWidth(
      handleRef.current?.parentElement?.parentElement?.getBoundingClientRect()
        .width || 0
    );

    if (onStartResize) onStartResize();
    if (onResize) onResize(width(event.clientX, startX - event.clientX));

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    event.stopPropagation();
    event.preventDefault();
  };

  const onMouseMove = (event: MouseEvent) => {
    if (dragging) {
      event.stopPropagation();
      event.preventDefault();

      if (onResize) onResize(width(event.pageX, startX - event.clientX));

      event.stopPropagation();
      event.preventDefault();
    }
  };

  const onMouseUp = (event: MouseEvent) => {
    if (dragging) {
      setDragging(false);
      if (onEndResize) onEndResize();
      if (onResize) onResize(width(event.clientX, startX - event.clientX));

      event.stopPropagation();
      event.preventDefault();
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  };

  useEventHandler('mousemove', (event: MouseEvent) => onMouseMove(event));
  useEventHandler('mouseup', (event: MouseEvent) => onMouseUp(event));

  return (
    <div
      className={classNames(styles.resizer, {
        [styles.floatingResizer]: floating,
      })}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
    >
      <div
        ref={handleRef}
        className={classNames({
          [styles.handle]: true,
          [styles.dragging]: dragging,
        })}
      />
    </div>
  );
};

export default ApplicationView(ResizerHandle);
